import { NextPage } from "next";
import { useIntl } from "react-intl";
import Head from "next/head";
import { PageProps } from "@/pages/_app";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/interfaces/seo";
import { SEOWithTemplate } from "@/interfaces/seoWithTemplate";
import HeroBlock, {
  HeroBlockProps,
} from "@/components/common/heroBlock/HeroBlock";
import {
  PointOfInterestList,
  PointOfInterestListProps,
} from "@/components/common/pointOfInterestList/PointOfInterestList";
import { Section } from "@/components/common/section/Section";
import { StaticMap } from "@/components/common/staticMap/StaticMap";
import { CityDetails } from "@/interfaces/cityDetails";
import { CardCarrousel } from "@/components/common/cardCarrousel/CardCarrousel";
import {
  DefaultPresentation,
  DefaultPresentationProps,
} from "@/components/common/defaultPresentation/DefaultPresentation";
import { getNonEmptyString } from "@/utils/getNonEmptyString";
import { VehicleType } from "@/components/common/searchBlock/children/vehicleTypeSelection/VehicleTypeSelection";
import { FAQProps } from "@/utils/formatCategoryPage";
import { DescriptionWithArticle } from "@/components/common/descriptionWithArticle/DescriptionWithArticle";
import { DescriptionWithArticleProps } from "@/strapiQuery/_interfaces";

import { Feature, isFeatureEnabled } from "@/utils/isFeatureEnabled";
import ReviewsBlock2 from "@/components/common/reviewsBlockV1/ReviewsBlock";
import ReviewsBlock1 from "@/components/common/reviewsBlock/ReviewsBlock";
import { StructuredReviewProps } from "@/components/common/structuredData/StructuredData.interface";
import styles from "./CityPage.module.scss";
import { FAQ } from "../../vehicle/categoryPage/faq/FAQ";

const ReviewsBlock = ({
  name,
  agency,
  rentACarReview,
  reviewsConst,
  image,
}: StructuredReviewProps) => {
  if (!isFeatureEnabled(Feature.NEW_REVIEWS_BLOCK)) {
    return (
      <ReviewsBlock1
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  } else {
    return (
      <ReviewsBlock2
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  }
};

export type CityPageContent = {
  title: string;
  pointOfInterestList: PointOfInterestListProps | null;
  cityDescription: DescriptionWithArticleProps[] | undefined;
  heroBlock?: HeroBlockProps;
  faq?: FAQProps;
  seo: SEO;
  seoWithTemplate: SEOWithTemplate;
};
type CityPageProps = PageProps &
  CityPageContent & {
    slug: string;
    city: CityDetails;
    defaultPresentation: DefaultPresentationProps;
    defaultCanonicalRoute: string;
  };

const hostUrl = process.env.NEXT_PUBLIC_HOST_URL ?? "";

export const CityPage: NextPage<CityPageProps> = ({
  layout,
  reviewsConst,
  defaultPresentation,
  city,
  slug,
  pointOfInterestList,
  faq,
  heroBlock,
  cityDescription,
  seo,
  seoWithTemplate,
  defaultCanonicalRoute,
}) => {
  const intl = useIntl();

  const departmentBreadcumbURL =
    city.state.slug !== "75"
      ? `${hostUrl}/agences/departement/${city.state.slug}`
      : "#";

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: intl.formatMessage({ id: "home" }),
                  item: hostUrl,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: intl.formatMessage({ id: "agenciesBreadcrumb" }),
                  item: `${hostUrl}/agences`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: city.state.name,
                  item: `${hostUrl}/agences/departement/${city.state.slug}`,
                },
                {
                  "@type": "ListItem",
                  position: 4,
                  name: city.name,
                  item: `${hostUrl}/agences/v/${slug}`,
                },
              ],
            }),
          }}
        />
      </Head>
      <Layout
        layout={layout}
        pageMeta={seo}
        title={
          seoWithTemplate.overrideTitle ??
          intl.formatMessage(
            { id: "defaultMetaTitle" },
            {
              name: getNonEmptyString(
                seoWithTemplate.valueInTemplateTitle,
                city.name
              ),
            }
          )
        }
        description={
          seoWithTemplate.overrideDescription ??
          intl.formatMessage(
            { id: "defaultMetaDescription" },
            {
              name: getNonEmptyString(
                seoWithTemplate.valueInTemplateDescription,
                city.name
              ),
              additional: city.state.name,
            }
          )
        }
        breadcrumbs={[
          { title: intl.formatMessage({ id: "home" }), url: "/" },
          {
            title: intl.formatMessage({ id: "agenciesBreadcrumb" }),
            url: `${hostUrl}/agences`,
          },
          {
            title: city.state.name,
            url: departmentBreadcumbURL,
          },
          {
            title: city.name,
            url: `${hostUrl}/agences/v/${slug}`,
          },
        ]}
        defaultCanonicalRoute={defaultCanonicalRoute}
      >
        <HeroBlock
          title={
            heroBlock?.title ??
            intl.formatMessage(
              { id: "cityPageTitle" },
              {
                city: city.name,
                br: () => <br />,
              }
            )
          }
          searchBlockInitialState={{
            vehicleType: VehicleType.CAR,
            isOneWayTrip: false,
          }}
        />
        <Section>
          <StaticMap
            mapName={"toutes-les-agences-rent-a-car-v2_1118492"}
            forceX={city.latitude}
            forceY={city.longitude}
            forceZoom={"11"}
            folder={"cities"}
          />
        </Section>
        <Section>
          {pointOfInterestList !== null &&
            pointOfInterestList.pointOfInterests.length > 0 && (
              <PointOfInterestList
                location={city.name}
                pointOfInterestList={pointOfInterestList}
              />
            )}
          {(!pointOfInterestList ||
            pointOfInterestList.pointOfInterests.length === 0) && (
            <DefaultPresentation {...defaultPresentation} name={city.name} />
          )}
          {cityDescription?.map((cat, index: number) => (
            <DescriptionWithArticle {...cat} key={index} />
          ))}
          <div className={styles.divider}></div>
          {city.stateAgencies.length > 0 && (
            <CardCarrousel
              title={intl.formatMessage(
                { id: "agenciesOf" },
                { name: city.state.name }
              )}
              urlPrefix={`${hostUrl}/agences/`}
              cards={city.stateAgencies}
            />
          )}
        </Section>

        {faq && <FAQ {...faq} />}

        {city.reviews?.clientReviews && city.reviews.clientReviews.length && (
          <ReviewsBlock
            name={city.name}
            rentACarReview={city.reviews}
            reviewsConst={reviewsConst}
          />
        )}
      </Layout>
    </>
  );
};
